import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { services } from "@/mocks/home";

export default function ServicesSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.08 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-24 md:py-32 bg-background-50"
    >
      <div className="px-6 lg:px-12 xl:px-20">
        <div className="reveal text-center mb-16 md:mb-20">
          <span className="text-xs font-semibold tracking-[0.2em] text-primary-500 uppercase mb-4 block">
            Capabilities
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 tracking-tight">
            {t("services.title")}
          </h2>
          <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {t("services.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="reveal group bg-white border border-foreground-100/60 rounded-xl p-7 md:p-8 hover:border-foreground-200 transition-all duration-300 flex flex-col"
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="w-11 h-11 rounded-lg bg-foreground-950 flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${service.icon} text-lg text-white`} />
                </div>
              </div>

              <h3 className="text-lg font-bold text-foreground-950 mb-3 tracking-tight">
                {service.title}
              </h3>

              <p className="text-sm text-foreground-500 leading-relaxed mb-5 flex-grow">
                {service.description}
              </p>

              <div className="space-y-2.5">
                {service.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-2.5 text-sm text-foreground-600">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-primary-500 text-xs" />
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}